import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { Zap, Coins, Home, Play, Trophy, RotateCcw } from 'lucide-react';

const API_URL = 'https://ahantravyuh.com/helicopter/api';

// Game constants
const LANE_WIDTH = 2;
const LANES = [-LANE_WIDTH, 0, LANE_WIDTH];
const JUMP_FORCE = 0.4;
const GRAVITY = 0.02;
const INITIAL_GAME_SPEED = 0.15;
const SPEED_INCREMENT = 0.00002;

interface UserInfo {
  ahn_id: string;
  name: string;
  ahn_balance: number;
  urja_balance: number;
}

interface DailyStats {
  urja_earned: number;
  games_played: number;
  best_distance: number;
  urja_remaining: number;
  daily_cap: number;
}

interface LeaderboardEntry {
  rank: number;
  name?: string;
  distance: number;
}

type GameState = 'menu' | 'playing' | 'gameover';

function App() {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [gameState, setGameState] = useState<GameState>('menu');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [distance, setDistance] = useState(0);
  const [gems, setGems] = useState(0);
  const [lastUrja, setLastUrja] = useState(0);
  const [isNewBest, setIsNewBest] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);

  // Three.js refs
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const playerRef = useRef<THREE.Object3D | null>(null);  // Changed to Object3D to support both Mesh and GLTF models
  const obstaclesRef = useRef<THREE.Mesh[]>([]);
  const gemsRef = useRef<THREE.Mesh[]>([]);
  const groundsRef = useRef<THREE.Mesh[]>([]);

  // Game state refs
  const animationRef = useRef<number>(0);
  const gameStateRef = useRef<GameState>('menu');
  const speedRef = useRef(INITIAL_GAME_SPEED);
  const distanceRef = useRef(0);
  const gemsRef2 = useRef(0);
  const laneRef = useRef(1);
  const velocityYRef = useRef(0);
  const jumpingRef = useRef(false);
  const frameRef = useRef(0);

  // Sync gameState to ref
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // Fetch user data
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    if (token) {
      fetch(`${API_URL}/me`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => d.success && setUser(d.user))
        .catch(console.error);
      fetch(`${API_URL}/me/daily`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => d.success && setDailyStats(d.daily))
        .catch(console.error);
    }
    fetch(`${API_URL}/leaderboard`)
      .then(r => r.json())
      .then(d => d.success && setLeaderboard(d.leaderboard))
      .catch(console.error);
  }, []);

  // Initialize Three.js
  useEffect(() => {
    if (!containerRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    scene.fog = new THREE.Fog(0x1a1a2e, 20, 60);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.set(0, 4, -8);
    camera.lookAt(0, 1, 5);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const sun = new THREE.DirectionalLight(0xffffff, 0.8);
    sun.position.set(5, 10, -5);
    scene.add(sun);

    // Ground
    const groundGeo = new THREE.PlaneGeometry(8, 20);
    const groundMat = new THREE.MeshStandardMaterial({ color: 0x2d5a27 });
    for (let i = 0; i < 5; i++) {
      const ground = new THREE.Mesh(groundGeo, groundMat);
      ground.rotation.x = -Math.PI / 2;
      ground.position.set(0, 0, i * 20);
      scene.add(ground);
      groundsRef.current.push(ground);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // 🎮 PLAYER CHARACTER - REPLACE THIS SECTION FOR NEW GAME VARIATIONS
    // ═══════════════════════════════════════════════════════════════════════
    // TO CREATE A NEW GAME (plane, train, car, etc.):
    // 1. Replace the model path: '/hanuman.glb' → '/yourmodel.glb'
    // 2. Adjust scale if needed: player.scale.set(0.5, 0.5, 0.5)
    // 3. Adjust position/rotation if needed
    // 4. Keep everything else the same - the game engine handles the rest!
    // ═══════════════════════════════════════════════════════════════════════

    const loader = new GLTFLoader();
    loader.load(
      process.env.PUBLIC_URL + '/helicopter.glb',  // 👈 CHANGE THIS PATH for different games (e.g., '/plane.glb', '/train.glb')
      (gltf) => {
        const player = gltf.scene;

        // Adjust size and position - CUSTOMIZE THESE for different models
        player.scale.set(0.6, 0.6, 0.6);  // 👈 Adjust scale to fit game
        player.position.set(0, 1.2, 0);      // 👈 Y-position: height above ground

        // Optional: Rotate model if it faces wrong direction
        player.rotation.y = Math.PI;  // Uncomment if model faces backward

        scene.add(player);
        playerRef.current = player;

        // Scene ready after model loads
        renderer.render(scene, camera);
        setSceneReady(true);
      },
      undefined,
      (error) => {
        console.error('Error loading player model:', error);
        // Fallback: Create simple capsule if model fails to load
        const playerGeo = new THREE.CapsuleGeometry(0.4, 1, 8, 16);
        const playerMat = new THREE.MeshStandardMaterial({ color: 0xff6600, emissive: 0x331100 });
        const player = new THREE.Mesh(playerGeo, playerMat);
        player.position.set(0, 1, 0);
        scene.add(player);
        playerRef.current = player;
        renderer.render(scene, camera);
        setSceneReady(true);
      }
    );

    // ═══════════════════════════════════════════════════════════════════════
    // END OF PLAYER CHARACTER SECTION
    // Everything below this is universal game engine code - DO NOT MODIFY
    // ═══════════════════════════════════════════════════════════════════════

    // Handle resize
    const onResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
      renderer.render(scene, camera);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(animationRef.current);
      renderer.dispose();
      if (containerRef.current && renderer.domElement.parentNode === containerRef.current) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, []);

  // Game loop
  const gameLoop = useCallback(() => {
    if (gameStateRef.current !== 'playing') return;
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current || !playerRef.current) return;

    frameRef.current++;
    const player = playerRef.current;
    const scene = sceneRef.current;

    // Speed up
    speedRef.current += SPEED_INCREMENT;
    distanceRef.current += speedRef.current * 10;

    // Move player to lane
    const targetX = LANES[laneRef.current];
    player.position.x += (targetX - player.position.x) * 0.15;

    // Jump
    if (jumpingRef.current) {
      velocityYRef.current -= GRAVITY;
      player.position.y += velocityYRef.current;
      if (player.position.y <= 1) {
        player.position.y = 1;
        jumpingRef.current = false;
        velocityYRef.current = 0;
      }
    }

    // Move ground
    groundsRef.current.forEach(g => {
      g.position.z -= speedRef.current;
      if (g.position.z < -20) g.position.z += 100;
    });

    // Move obstacles & check collision
    for (let i = obstaclesRef.current.length - 1; i >= 0; i--) {
      const obs = obstaclesRef.current[i];
      obs.position.z -= speedRef.current;

      const dx = Math.abs(obs.position.x - player.position.x);
      const dz = Math.abs(obs.position.z - player.position.z);
      const h = obs.userData.height || 1;

      if (dx < 0.8 && dz < 0.8 && player.position.y < h + 0.5) {
        gameOver();
        return;
      }

      if (obs.position.z < -10) {
        scene.remove(obs);
        obstaclesRef.current.splice(i, 1);
      }
    }

    // Move gems & check collection
    for (let i = gemsRef.current.length - 1; i >= 0; i--) {
      const gem = gemsRef.current[i];
      gem.position.z -= speedRef.current;
      gem.rotation.y += 0.05;

      const dx = Math.abs(gem.position.x - player.position.x);
      const dz = Math.abs(gem.position.z - player.position.z);

      if (dx < 1 && dz < 1) {
        gemsRef2.current += gem.userData.value;
        scene.remove(gem);
        gemsRef.current.splice(i, 1);
        continue;
      }

      if (gem.position.z < -10) {
        scene.remove(gem);
        gemsRef.current.splice(i, 1);
      }
    }

    // Spawn obstacles
    if (frameRef.current % 60 === 0) {
      const lane = Math.floor(Math.random() * 3);
      const type = Math.random();
      let geo, color, height;

      if (type < 0.4) {
        geo = new THREE.BoxGeometry(1.2, 1, 1.2);
        color = 0x666666;
        height = 1;
      } else if (type < 0.7) {
        geo = new THREE.CylinderGeometry(0.4, 0.5, 1.5, 8);
        color = 0xff4400;
        height = 1.5;
      } else {
        geo = new THREE.BoxGeometry(0.8, 2.5, 0.8);
        color = 0x884422;
        height = 2.5;
      }

      const obs = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color }));
      obs.position.set(LANES[lane], height / 2, 50);
      obs.userData.height = height;
      scene.add(obs);
      obstaclesRef.current.push(obs);
    }

    // Spawn gems
    if (frameRef.current % 40 === 0) {
      const lane = Math.floor(Math.random() * 3);
      const rare = Math.random() < 0.1;
      const gem = new THREE.Mesh(
        new THREE.SphereGeometry(0.3, 16, 16),
        new THREE.MeshStandardMaterial({ color: rare ? 0x00ff88 : 0xffdd00, emissive: rare ? 0x004422 : 0x443300 })
      );
      gem.position.set(LANES[lane], 1.2, 50);
      gem.userData.value = rare ? 50 : 10;
      scene.add(gem);
      gemsRef.current.push(gem);
    }

    // Update UI
    if (frameRef.current % 10 === 0) {
      setDistance(Math.floor(distanceRef.current));
      setGems(gemsRef2.current);
    }

    rendererRef.current.render(sceneRef.current, cameraRef.current);
    animationRef.current = requestAnimationFrame(gameLoop);
  }, []);

  // Game over
  const gameOver = useCallback(async () => {
    setGameState('gameover');
    cancelAnimationFrame(animationRef.current);

    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await fetch(`${API_URL}/game/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ distance: Math.floor(distanceRef.current), gems_collected: gemsRef2.current })
        });
        const data = await res.json();
        if (data.success) {
          setLastUrja(data.urja_earned);
          setIsNewBest(data.is_new_best);
          // Refresh user data
          fetch(`${API_URL}/me`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => d.success && setUser(d.user));
          fetch(`${API_URL}/me/daily`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => d.success && setDailyStats(d.daily));
        }
      } catch (e) {
        console.error(e);
      }
    }
    fetch(`${API_URL}/leaderboard`).then(r => r.json()).then(d => d.success && setLeaderboard(d.leaderboard));
  }, []);

  // Start game
  const startGame = useCallback(() => {
    if (!sceneRef.current || !playerRef.current) {
      console.error('Scene not ready');
      return;
    }

    // Reset
    speedRef.current = INITIAL_GAME_SPEED;
    distanceRef.current = 0;
    gemsRef2.current = 0;
    laneRef.current = 1;
    velocityYRef.current = 0;
    jumpingRef.current = false;
    frameRef.current = 0;
    setDistance(0);
    setGems(0);
    setLastUrja(0);
    setIsNewBest(false);

    // Clear objects
    obstaclesRef.current.forEach(o => sceneRef.current?.remove(o));
    gemsRef.current.forEach(g => sceneRef.current?.remove(g));
    obstaclesRef.current = [];
    gemsRef.current = [];

    // Reset player
    playerRef.current.position.set(0, 1, 0);

    setGameState('playing');
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [gameLoop]);

  // Controls
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (gameStateRef.current !== 'playing') return;
      if (e.key === 'ArrowLeft' || e.key === 'a') laneRef.current = Math.max(0, laneRef.current - 1);
      if (e.key === 'ArrowRight' || e.key === 'd') laneRef.current = Math.min(2, laneRef.current + 1);
      if ((e.key === 'ArrowUp' || e.key === 'w' || e.key === ' ') && !jumpingRef.current) {
        jumpingRef.current = true;
        velocityYRef.current = JUMP_FORCE;
      }
    };

    let touchX = 0, touchY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (gameStateRef.current !== 'playing') return;
      const dx = e.changedTouches[0].clientX - touchX;
      const dy = e.changedTouches[0].clientY - touchY;
      if (Math.abs(dx) > Math.abs(dy)) {
        if (dx > 30) laneRef.current = Math.max(0, laneRef.current - 1);
        if (dx < -30) laneRef.current = Math.min(2, laneRef.current + 1);
      } else if (dy < -30 && !jumpingRef.current) {
        jumpingRef.current = true;
        velocityYRef.current = JUMP_FORCE;
      }
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <div className="h-screen bg-slate-900 text-slate-100 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-orange-400">HELICOPTER</h1>
              <p className="text-xs text-slate-400">Fly. Dodge. Survive.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user && (
              <>
                <div className="bg-slate-700 px-2 py-1 rounded text-sm hidden sm:block">
                  <Coins className="w-4 h-4 text-yellow-400 inline mr-1" />{user.ahn_balance.toFixed(0)}
                </div>
                <div className="bg-slate-700 px-2 py-1 rounded text-sm hidden sm:block">
                  <Zap className="w-4 h-4 text-purple-400 inline mr-1" />{user.urja_balance.toFixed(0)}
                </div>
              </>
            )}
            <button onClick={() => setShowLeaderboard(true)} className="p-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg">
              <Trophy className="w-5 h-5" />
            </button>
            <button onClick={() => window.location.href = '/'} className="p-2 bg-purple-600 hover:bg-purple-700 rounded-lg">
              <Home className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Game */}
      <div className="flex-1 relative">
        <div ref={containerRef} className="w-full h-full" />

        {/* HUD */}
        {gameState === 'playing' && (
          <div className="absolute top-4 left-4 right-4 flex justify-between pointer-events-none">
            <div className="bg-black/50 px-4 py-2 rounded-lg">
              <div className="text-2xl font-bold text-orange-400">{distance}m</div>
            </div>
            <div className="bg-black/50 px-4 py-2 rounded-lg">
              <span className="text-yellow-400 text-xl">+{gems}</span>
            </div>
          </div>
        )}

        {/* Menu */}
        {gameState === 'menu' && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <div className="text-center p-8">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Play className="w-10 h-10 text-white ml-1" />
              </div>
              <h2 className="text-3xl font-bold text-orange-400 mb-2">HELICOPTER</h2>
              <p className="text-slate-400 mb-6">Fly high, dodge obstacles, earn URJA!</p>

              {isLoggedIn ? (
                <>
                  <button
                    onClick={startGame}
                    disabled={!sceneReady}
                    className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-slate-600 text-white text-xl font-bold py-4 px-8 rounded-xl mb-4"
                  >
                    {sceneReady ? 'START GAME' : 'Loading...'}
                  </button>
                  {dailyStats && (
                    <p className="text-sm text-slate-400">Today: {dailyStats.urja_earned}/{dailyStats.daily_cap} URJA</p>
                  )}
                </>
              ) : (
                <button onClick={() => window.location.href = '/'} className="bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-lg">
                  Login to Play
                </button>
              )}

              <p className="mt-4 text-xs text-slate-500">Controls: Arrow keys / WASD / Swipe</p>
            </div>
          </div>
        )}

        {/* Game Over */}
        {gameState === 'gameover' && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
            <div className="text-center p-8 bg-slate-800 rounded-2xl border border-slate-700 max-w-sm">
              <h2 className="text-3xl font-bold text-red-400 mb-4">GAME OVER</h2>

              {isNewBest && (
                <div className="bg-yellow-600/20 border border-yellow-600 text-yellow-400 px-4 py-2 rounded-lg mb-4">
                  NEW BEST!
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <div className="text-3xl font-bold text-orange-400">{distance}m</div>
                  <div className="text-xs text-slate-400">Distance</div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <div className="text-3xl font-bold text-yellow-400">{gems}</div>
                  <div className="text-xs text-slate-400">Gems</div>
                </div>
              </div>

              {lastUrja > 0 && (
                <div className="bg-purple-600/20 border border-purple-600 rounded-lg p-3 mb-4">
                  <Zap className="w-5 h-5 text-purple-400 inline mr-2" />
                  <span className="text-xl font-bold text-purple-400">+{lastUrja} URJA</span>
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={startGame} className="flex-1 bg-orange-600 hover:bg-orange-700 py-3 rounded-xl flex items-center justify-center gap-2">
                  <RotateCcw className="w-5 h-5" /> Again
                </button>
                <button onClick={() => setGameState('menu')} className="bg-slate-700 hover:bg-slate-600 px-4 py-3 rounded-xl">
                  Menu
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        {showLeaderboard && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 max-w-sm w-full max-h-[80vh] overflow-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
                  <Trophy className="w-5 h-5" /> Leaderboard
                </h2>
                <button onClick={() => setShowLeaderboard(false)} className="text-slate-400 hover:text-white text-2xl">&times;</button>
              </div>
              {leaderboard.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No scores yet!</p>
              ) : (
                <div className="space-y-2">
                  {leaderboard.slice(0, 15).map((e, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${i < 3 ? 'bg-yellow-600/20' : 'bg-slate-700/50'}`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-yellow-500 text-black' : i === 1 ? 'bg-slate-400 text-black' : i === 2 ? 'bg-orange-600' : 'bg-slate-600'}`}>
                          {e.rank}
                        </span>
                        <span>{e.name || 'Player'}</span>
                      </div>
                      <span className="text-orange-400 font-bold">{e.distance}m</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
